import React, { useEffect, useState } from "react";
import { CreditCard, Calendar, IndianRupee, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { payViaRazorpay } from "@/lib/razorpay";

const STATUS_STYLE = {
  paid: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-50 text-amber-700",
  pending: "bg-rose-50 text-rose-700",
};

export default function AdminSubscription() {
  const { user, tenant } = useAuth();
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => api.get("/payments/subscription").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const payDue = async () => {
    if (!data?.due_amount) return toast.info("No dues to pay");
    setPaying(true);
    try {
      const res = await payViaRazorpay({
        amount: data.due_amount, purpose: "subscription",
        name: tenant?.brand_name || "Subscription Payment",
        user: { name: user?.full_name, phone: user?.phone },
      });
      toast.success(res.mocked ? `(MOCK) Subscription paid ₹${data.due_amount}` : `Subscription paid ₹${data.due_amount}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Payment failed"); }
    finally { setPaying(false); }
  };

  if (!data) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="admin-subscription-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight">Subscription</h1>
        <p className="text-sm text-slate-500">Pay your platform subscription to keep your tenant active.</p>
      </div>

      <div className={`rounded-2xl p-6 card-shadow ${data.due_amount > 0 ? "bg-amber-500 text-white" : "bg-emerald-600 text-white"}`} data-testid="subscription-status-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-80 font-bold">Current Status</div>
            <div className="text-3xl font-extrabold tracking-tight mt-1 capitalize">{data.payment_status}</div>
            <div className="text-xs opacity-90 mt-1">Active until {fmtDate(data.subscription_end)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-80 font-bold">Due Amount</div>
            <div data-testid="due-amount" className="text-3xl font-extrabold tracking-tight mt-1">{fmtINR(data.due_amount)}</div>
            {data.due_amount > 0 && (
              <button data-testid="pay-due" onClick={payDue} disabled={paying} className="mt-3 bg-white text-slate-900 px-4 py-2 rounded-xl font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60">
                <CreditCard className="w-4 h-4" /> {paying ? "Processing..." : "Pay Now"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Subscription" value={fmtINR(data.subscription_amount)} icon={IndianRupee} />
        <Stat label="Renewal" value={fmtINR(data.renewal_amount)} icon={IndianRupee} />
        <Stat label="Paid" value={fmtINR(data.paid_amount)} icon={CheckCircle2} />
        <Stat label="Period" value={`${fmtDate(data.subscription_start)} → ${fmtDate(data.subscription_end)}`} icon={Calendar} />
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
        <h2 className="font-extrabold mb-3">Payment History</h2>
        <div className="table-scroll">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Method</th>
                <th className="text-left p-3">Reference</th>
                <th className="text-right p-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data.history || []).map((h) => (
                <tr key={h.id} data-testid={`payment-${h.id}`} className="hover:bg-slate-50">
                  <td className="p-3 text-[12px]">{fmtDate(h.created_at)}</td>
                  <td className="p-3 text-[11px] uppercase tracking-wider text-slate-500">{h.method}</td>
                  <td className="p-3 text-[12px] font-mono text-slate-600">{h.reference}</td>
                  <td className="p-3 text-right font-extrabold text-emerald-600">{fmtINR(h.amount)}</td>
                </tr>
              ))}
              {(data.history || []).length === 0 && (
                <tr><td colSpan={4} className="text-center p-6 text-slate-400">No payments recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="bg-white border border-slate-100 rounded-xl p-3 card-shadow">
    <Icon className="w-4 h-4 text-slate-400" />
    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mt-1">{label}</div>
    <div className="text-sm font-extrabold mt-0.5">{value}</div>
  </div>
);
